import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface RefundResult {
  orderId: string;
  refundId: string;
  amount: number;
  currency: string;
  createdAt: string;
  status: 'pending' | 'succeeded' | 'failed';
  message?: string;
}

/** PHP payload (we’ll see "success" | "failed") */
interface FullRefundApiResponse {
  success?: boolean; // occasionally used
  status?: 'success' | 'failed' | 'pending' | 'succeeded' | string;
  message?: string;
  refundId?: string;
  amount?: number;
  currency?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RefundService {
  constructor(private http: HttpClient) {}

  fullRefund(
    orderId: number | string,
    comment?: string
  ): Observable<RefundResult> {
    let body = new HttpParams()
      .set('action', 'fullRefund')
      .set('orderId', String(parseInt(String(orderId), 10)));

    const commentTrimmed = (comment ?? '').trim();
    if (commentTrimmed) {
      body = body.set('comment', commentTrimmed);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.http
      .post<FullRefundApiResponse>(environment.orderUrl, body.toString(), {
        headers,
      })
      .pipe(
        map((resp: FullRefundApiResponse) => {
          const raw = String(resp?.status ?? '').toLowerCase();
          const ok =
            raw === 'success' || raw === 'succeeded' || resp?.success === true;

          // Normalize to our enum
          const status: RefundResult['status'] = ok
            ? 'succeeded'
            : raw === 'pending'
            ? 'pending'
            : 'failed';

          return {
            orderId: String(orderId),
            refundId: resp?.refundId ?? '',
            amount: resp?.amount ?? 0,
            currency: resp?.currency ?? 'GBP',
            createdAt: new Date().toISOString(),
            status,
            message: resp?.message,
          };
        })
      );
  }

  partialRefund(
    orderId: number | string,
    amount: number,
    comment?: string
  ): Observable<RefundResult> {
    let body = new HttpParams()
      .set('action', 'partialRefundStaticAmount')
      .set('orderId', String(parseInt(String(orderId), 10)))
      // send as major units with 2dp (e.g. "12.34")
      .set('refundAmount', amount.toFixed(2));

    const commentTrimmed = (comment ?? '').trim();
    if (commentTrimmed) body = body.set('comment', commentTrimmed);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.http
      .post<FullRefundApiResponse>(environment.orderUrl, body.toString(), {
        headers,
      })
      .pipe(map((resp) => this.normalize(orderId, resp, amount)));
  }

  itemRefund(
    orderId: number | string,
    orderItems: Array<{ id: number; qty: number }>,
    comment?: string,
    fallbackAmount?: number
  ): Observable<RefundResult> {
    const payload: any = {
      action: 'itemBasedRefund',
      orderId: parseInt(String(orderId), 10),
      orderItems,
    };
    const commentTrimmed = (comment ?? '').trim();
    if (commentTrimmed) payload.comment = commentTrimmed;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http
      .post<FullRefundApiResponse>(environment.orderUrl, payload, { headers })
      .pipe(map((resp) => this.normalize(orderId, resp, fallbackAmount)));
  }

  /** Shared normalizer */
  private normalize(
    orderId: number | string,
    resp: FullRefundApiResponse,
    fallbackAmount?: number
  ): RefundResult {
    const raw = String(resp?.status ?? '').toLowerCase();
    const ok =
      raw === 'success' || raw === 'succeeded' || resp?.success === true;

    const status: RefundResult['status'] = ok
      ? 'succeeded'
      : raw === 'pending'
      ? 'pending'
      : 'failed';

    return {
      orderId: String(orderId),
      refundId: resp?.refundId ?? '',
      amount: resp?.amount ?? fallbackAmount ?? 0,
      currency: resp?.currency ?? 'GBP',
      createdAt: new Date().toISOString(),
      status,
      message: resp?.message,
    };
  }
}
